import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  Send,
  BarChart,
  Search,
  Inbox,
  Upload,
  Download,
  Tag,
  X,
  Pause,
  Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Pagination, usePagination } from "../components/ui/pagination";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import {
  loadContacts,
  saveContacts,
  contactCount,
  toNameMap,
  resolveName,
  parseContactsCsv,
} from "../lib/contacts";

const API_BASE_URL = "https://email-backend.heybuddy.co.in";

const statusVariant = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "sent" || s === "completed") return "success";
  if (s === "sending" || s === "scheduled") return "warning";
  if (s === "paused") return "outline";
  if (s === "failed") return "destructive";
  return "secondary";
};

const DATE_PRESETS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "year", label: "This year" },
  { value: "custom", label: "Custom range" },
];

const withinDateRange = (dateStr, preset, from, to) => {
  if (preset === "all" || !dateStr) return true;
  const d = new Date(dateStr);
  const now = new Date();
  if (preset === "custom") {
    if (from && d < new Date(from)) return false;
    if (to) {
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);
      if (d > end) return false;
    }
    return true;
  }
  if (preset === "year") return d.getFullYear() === now.getFullYear();
  const days = { "7d": 7, "30d": 30, "90d": 90 }[preset];
  if (days) {
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - days);
    return d >= cutoff;
  }
  return true;
};

const downloadCsv = (rows, filename) => {
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // create / edit
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    templateId: "",
    category: "",
  });

  // send
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");

  // export
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState("campaign");
  const [exportCampaignId, setExportCampaignId] = useState("");
  const [exportCategory, setExportCategory] = useState("");
  const [exportMetric, setExportMetric] = useState("opens");
  const [exportComparator, setExportComparator] = useState("gt");
  const [exportThreshold, setExportThreshold] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(contactCount());

  const navigate = useNavigate();

  const handleContactsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const incoming = await parseContactsCsv(file);
      const merged = { ...loadContacts(), ...incoming };
      saveContacts(merged);
      setContactsLoaded(Object.keys(merged).length);
      toast.success(`${Object.keys(incoming).length} names added to your contact list`);
    } catch (err) {
      toast.error(err.message || "Failed to read contacts CSV");
    }
    e.target.value = "";
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchSenderAccounts();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/campaigns`);
      setCampaigns(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to fetch campaigns");
      setIsLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/email-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching email templates:", error);
      toast.error("Failed to fetch email templates");
    }
  };

  const fetchSenderAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/emails`);
      setSenderAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedSenderEmail(response.data[0].email);
      }
    } catch (error) {
      console.error("Error fetching sender accounts:", error);
      toast.error("Failed to fetch sender accounts");
    }
  };

  // distinct categories present across campaigns
  const categories = useMemo(
    () =>
      [...new Set(campaigns.map((c) => (c.category || "").trim()).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [campaigns]
  );

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, category: formData.category?.trim() || "" };
    try {
      if (currentCampaign) {
        await axios.put(
          `${API_BASE_URL}/api/campaigns/${currentCampaign._id}`,
          payload
        );
        toast.success("Campaign updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/api/campaigns`, payload);
        toast.success("Campaign created successfully");
      }
      setIsDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error("Error submitting campaign:", error);
      toast.error(error.response?.data?.message || "Failed to submit campaign");
    }
  };

  const handleEdit = (campaign) => {
    setCurrentCampaign(campaign);
    setFormData({
      name: campaign.name,
      subject: campaign.subject,
      templateId: campaign.templateId?._id || campaign.templateId || "",
      category: campaign.category || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/campaigns/${id}`);
        toast.success("Campaign deleted successfully");
        fetchCampaigns();
      } catch (error) {
        console.error("Error deleting campaign:", error);
        toast.error("Failed to delete campaign");
      }
    }
  };

  const handleSend = (campaign) => {
    setCurrentCampaign(campaign);
    setRecipients([]);
    setIsSendDialogOpen(true);
    setSendError("");
  };

  const handlePause = async (campaign) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/campaigns/${campaign._id}/pause`
      );
      const done = data?.progress?.totalProcessed;
      const total = data?.progress?.totalRecipients;
      toast.success(
        done != null && total != null
          ? `Campaign paused at ${done}/${total} emails`
          : "Campaign paused"
      );
      fetchCampaigns();
    } catch (error) {
      console.error("Error pausing campaign:", error);
      toast.error(error.response?.data?.message || "Failed to pause campaign");
    }
  };

  const handleResume = async (campaign) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/campaigns/${campaign._id}/resume`
      );
      toast.success(
        data?.remaining != null
          ? `Campaign resumed — ${data.remaining} emails remaining`
          : "Campaign resumed"
      );
      fetchCampaigns();
    } catch (error) {
      console.error("Error resuming campaign:", error);
      toast.error(error.response?.data?.message || "Failed to resume campaign");
    }
  };

  const handleSendSubmit = async () => {
    if (!selectedSenderEmail) {
      setSendError("Please select a sender account");
      return;
    }
    setIsSending(true);
    setSendError("");
    try {
      await axios.post(
        `${API_BASE_URL}/api/campaigns/${currentCampaign._id}/send`,
        { recipients, senderEmail: selectedSenderEmail }
      );
      toast.success("Campaign sending started");
      setIsSendDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error("Error sending campaign:", error);
      setSendError(error.response?.data?.message || "Failed to send campaign");
      toast.error(error.response?.data?.message || "Failed to send campaign");
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // First pass: parse with headers so we can honour named columns
    // (email / name / subject) in any order and case.
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => (h || "").trim(),
      complete: (results) => {
        const fields = (results.meta.fields || []).map((f) => (f || "").trim());
        const findKey = (needles) =>
          fields.find((f) => {
            const l = f.toLowerCase();
            return needles.some((n) => l.includes(n));
          });
        const emailKey = findKey(["email", "e-mail", "mail"]);
        const nameKey = findKey(["name"]);
        const subjectKey = findKey(["subject"]);

        if (emailKey) {
          const parsed = results.data
            .map((row) => ({
              id: (row[emailKey] || "").trim(),
              email: (row[emailKey] || "").trim(),
              name: nameKey ? (row[nameKey] || "").trim() : "",
              subject: subjectKey ? (row[subjectKey] || "").trim() : "",
            }))
            .filter((r) => r.email);
          setRecipients(parsed);
          const withSubject = parsed.filter((r) => r.subject).length;
          toast.success(
            `${parsed.length} recipients loaded${
              withSubject ? ` · ${withSubject} with a custom subject` : ""
            }`
          );
          return;
        }

        // Fallback: headerless / positional CSV -> [name, email]
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          complete: (raw) => {
            const parsed = raw.data
              .slice(1)
              .map((row) => ({
                id: row[1],
                email: (row[1] || "").trim(),
                name: (row[0] || "").trim(),
                subject: "",
              }))
              .filter((r) => r.email);
            setRecipients(parsed);
            toast.success(`${parsed.length} recipients loaded from CSV`);
          },
        });
      },
    });
  };

  const handleViewReport = (campaignId) => {
    navigate(`/reports/${campaignId}`);
  };

  // ---- conditional contact export ----
  const runExport = async () => {
    const targets =
      exportScope === "campaign"
        ? campaigns.filter((c) => c._id === exportCampaignId)
        : campaigns.filter((c) => (c.category || "").trim() === exportCategory);

    if (targets.length === 0) {
      toast.error(
        exportScope === "campaign"
          ? "Please select a campaign"
          : "No campaigns found in this category"
      );
      return;
    }

    const threshold = Number(exportThreshold) || 0;
    const passes = (n) =>
      exportComparator === "gt"
        ? n > threshold
        : exportComparator === "gte"
        ? n >= threshold
        : n === threshold;

    setExporting(true);
    try {
      const results = await Promise.allSettled(
        targets.map((c) => axios.get(`${API_BASE_URL}/api/reports/${c._id}`))
      );

      const master = loadContacts();
      const map = {}; // email -> { email, name, total, campaigns:Set }
      results.forEach((r, i) => {
        if (r.status !== "fulfilled") return;
        const report = r.value.data;
        const details =
          exportMetric === "clicks"
            ? report?.clicks?.details
            : report?.opens?.details;
        if (!Array.isArray(details)) return;

        // Send-time names (available after the backend deploy) take priority.
        const recipMap = toNameMap(report?.campaignId?.recipients);

        const perContact = {};
        details.forEach((d) => {
          if (d?.recipientId)
            perContact[d.recipientId] = (perContact[d.recipientId] || 0) + 1;
        });

        Object.entries(perContact).forEach(([email, count]) => {
          if (!passes(count)) return;
          if (!map[email]) {
            map[email] = {
              email,
              name: resolveName(email, recipMap, master),
              total: 0,
              campaigns: new Set(),
            };
          } else if (!map[email].name) {
            map[email].name = resolveName(email, recipMap, master);
          }
          map[email].total += count;
          map[email].campaigns.add(targets[i].name);
        });
      });

      const rows = Object.values(map)
        .sort((a, b) => b.total - a.total)
        .map((r) => ({
          name: r.name,
          email: r.email,
          [exportMetric]: r.total,
          campaigns: [...r.campaigns].join("; "),
          category: exportScope === "category" ? exportCategory : "",
        }));

      if (rows.length === 0) {
        toast.error("No contacts matched that condition");
        return;
      }

      const symbol = { gt: "gt", gte: "gte", eq: "eq" }[exportComparator];
      downloadCsv(rows, `contacts_${exportMetric}_${symbol}${threshold}.csv`);
      toast.success(`${rows.length} contact${rows.length > 1 ? "s" : ""} exported`);
      setExportOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to build export");
    } finally {
      setExporting(false);
    }
  };

  // ---- filtering ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (q) {
        const hit =
          c.name?.toLowerCase().includes(q) ||
          c.subject?.toLowerCase().includes(q) ||
          c.status?.toLowerCase().includes(q) ||
          c.category?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (categoryFilter !== "all") {
        if ((c.category || "").trim() !== categoryFilter) return false;
      }
      if (!withinDateRange(c.createdAt, datePreset, customFrom, customTo))
        return false;
      return true;
    });
  }, [campaigns, search, categoryFilter, datePreset, customFrom, customTo]);

  const { page, setPage, pageCount, total, pageSize, pageItems } =
    usePagination(filtered, 10);

  const activeFilters =
    (categoryFilter !== "all" ? 1 : 0) + (datePreset !== "all" ? 1 : 0);

  const clearFilters = () => {
    setCategoryFilter("all");
    setDatePreset("all");
    setCustomFrom("");
    setCustomTo("");
    setSearch("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">All Campaigns</h2>
          <p className="text-sm text-muted-foreground">
            Create, send and track your email campaigns.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)}>
            <Download className="mr-2 h-4 w-4" /> Export Contacts
          </Button>
          <Button
            onClick={() => {
              setCurrentCampaign(null);
              setFormData({ name: "", subject: "", templateId: "", category: "" });
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Create Campaign
          </Button>
        </div>
      </div>

      <Card>
        {/* Toolbar */}
        <div className="space-y-3 border-b p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative w-full lg:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search campaigns…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {activeFilters > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground"
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Clear
                </Button>
              )}
            </div>

            {!isLoading && (
              <span className="text-sm text-muted-foreground lg:ml-auto">
                {total} campaign{total !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {datePreset === "custom" && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">From</span>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-auto"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-auto"
              />
            </div>
          )}
        </div>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-5 w-full max-w-[130px]" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : pageItems.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6} className="py-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                          <Inbox className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-medium">
                          {search || activeFilters
                            ? "No matching campaigns"
                            : "No campaigns yet"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {search || activeFilters
                            ? "Try adjusting your filters."
                            : "Create your first campaign to get started."}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((campaign) => (
                    <TableRow key={campaign._id}>
                      <TableCell className="font-medium">{campaign.name}</TableCell>
                      <TableCell className="max-w-[240px] truncate text-muted-foreground">
                        {campaign.subject}
                      </TableCell>
                      <TableCell>
                        {campaign.category ? (
                          <Badge variant="outline" className="gap-1">
                            <Tag className="h-3 w-3" />
                            {campaign.category}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={statusVariant(campaign.status)}
                          className="capitalize"
                        >
                          {campaign.status || "draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {campaign.createdAt
                          ? new Date(campaign.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" title="Edit" onClick={() => handleEdit(campaign)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {campaign.status === "sending" ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Pause sending"
                              className="text-amber-600 hover:text-amber-700"
                              onClick={() => handlePause(campaign)}
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : campaign.status === "paused" ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Resume sending"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleResume(campaign)}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" title="Send" onClick={() => handleSend(campaign)}>
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" title="View report" onClick={() => handleViewReport(campaign._id)}>
                            <BarChart className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(campaign._id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isLoading && total > 0 && (
            <div className="border-t">
              <Pagination
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {currentCampaign ? "Edit Campaign" : "Create Campaign"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <label htmlFor="name" className="text-sm font-medium">Name</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="subject" className="text-sm font-medium">Subject</label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Template</label>
                  <Select
                    value={formData.templateId}
                    onValueChange={(value) =>
                      setFormData({ ...formData, templateId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      {templates.map((template) => (
                        <SelectItem key={template._id} value={template._id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="category" className="text-sm font-medium">
                    Category
                  </label>
                  <Input
                    id="category"
                    name="category"
                    list="campaign-category-options"
                    placeholder="e.g. Newsletter"
                    value={formData.category}
                    onChange={handleInputChange}
                  />
                  <datalist id="campaign-category-options">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
        <DialogContent className="bg-card sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Send Campaign</DialogTitle>
            {currentCampaign && (
              <p className="text-sm text-muted-foreground">{currentCampaign.name}</p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Sender Account</label>
              <Select value={selectedSenderEmail} onValueChange={setSelectedSenderEmail}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a sender account" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {senderAccounts.map((account) => (
                    <SelectItem key={account.email} value={account.email}>
                      {account.name} ({account.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="csvUpload" className="text-sm font-medium">
                Recipients CSV
              </label>
              <label
                htmlFor="csvUpload"
                className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed py-6 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                <Upload className="h-4 w-4" />
                {recipients.length > 0
                  ? `${recipients.length} recipients loaded — click to replace`
                  : "Click to upload a CSV file"}
              </label>
              <p className="text-xs text-muted-foreground">
                Columns: <code>name</code>, <code>email</code>, and an optional{" "}
                <code>subject</code> — rows with a subject override the campaign
                subject.
              </p>
              <Input
                id="csvUpload"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {recipients.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Preview</span>
                  {recipients.some((r) => r.subject) && (
                    <span className="text-xs text-muted-foreground">
                      {recipients.filter((r) => r.subject).length} custom subject
                      {recipients.filter((r) => r.subject).length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="max-h-[170px] overflow-y-auto rounded-lg border">
                  <ul className="divide-y">
                    {recipients.slice(0, 50).map((recipient, index) => (
                      <li key={index} className="px-3 py-1.5 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="truncate">{recipient.name || "—"}</span>
                          <span className="shrink-0 text-muted-foreground">
                            {recipient.email}
                          </span>
                        </div>
                        {recipient.subject && (
                          <div className="mt-0.5 truncate text-xs text-primary">
                            ✉ {recipient.subject}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {sendError && <div className="text-sm text-destructive">{sendError}</div>}
          </div>
          <DialogFooter>
            <Button
              onClick={handleSendSubmit}
              disabled={recipients.length === 0 || isSending || !selectedSenderEmail}
              className="w-full"
            >
              {isSending ? "Sending…" : "Send Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export contacts dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-card sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Export Contacts</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Download contacts that meet an engagement condition as CSV.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Scope</label>
              <Select value={exportScope} onValueChange={setExportScope}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="campaign">A single campaign</SelectItem>
                  <SelectItem value="category">A whole category</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportScope === "campaign" ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Campaign</label>
                <Select value={exportCampaignId} onValueChange={setExportCampaignId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a campaign" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[240px] bg-popover">
                    {campaigns.map((c) => (
                      <SelectItem key={c._id} value={c._id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Category</label>
                <Select value={exportCategory} onValueChange={setExportCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {categories.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No categories yet
                      </div>
                    ) : (
                      categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Contact names</p>
                <p className="text-xs text-muted-foreground">
                  {contactsLoaded > 0
                    ? `${contactsLoaded} names in your list — added to the export`
                    : "Optional: upload an email,name list to include names"}
                </p>
              </div>
              <label
                htmlFor="contactsUpload"
                className="shrink-0 cursor-pointer rounded-md border border-input px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {contactsLoaded > 0 ? "Update list" : "Upload list"}
              </label>
              <input
                id="contactsUpload"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleContactsUpload}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Condition</label>
              <div className="flex items-center gap-2">
                <Select value={exportMetric} onValueChange={setExportMetric}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="opens">Opens</SelectItem>
                    <SelectItem value="clicks">Clicks</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={exportComparator} onValueChange={setExportComparator}>
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="gt">{"greater >"}</SelectItem>
                    <SelectItem value="gte">{"at least ≥"}</SelectItem>
                    <SelectItem value="eq">{"equals ="}</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={0}
                  value={exportThreshold}
                  onChange={(e) => setExportThreshold(e.target.value)}
                  className="w-20"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                e.g. contacts whose {exportMetric}{" "}
                {exportComparator === "gt"
                  ? "are greater than"
                  : exportComparator === "gte"
                  ? "are at least"
                  : "equal"}{" "}
                {exportThreshold || 0}.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setExportOpen(false)}>
              Cancel
            </Button>
            <Button onClick={runExport} disabled={exporting}>
              <Download className="mr-2 h-4 w-4" />
              {exporting ? "Preparing…" : "Download CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPage;
